import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, userTypeLabel } from '../../utils/format';

const LIFESTYLE_LABELS = {
  college_year: { freshman: 'Freshman', sophomore: 'Sophomore', junior: 'Junior', senior: 'Senior', graduate: 'Grad Student', not_in_school: 'Not in School' },
  sleep_schedule: { early_bird: 'Early Bird', night_owl: 'Night Owl' },
  cleanliness: { very_clean: 'Very Clean', average: 'Average', relaxed: 'Relaxed' },
  noise_preference: { quiet: 'Quiet', social: 'Social' },
  guest_preference: { rarely: 'Rarely Has Guests', sometimes: 'Sometimes Has Guests', often: 'Often Has Guests' },
  study_habits: { at_home: 'Studies at Home', elsewhere: 'Studies Out' },
  pet_preference: { has_pets: 'Has Pets', no_pets: 'No Pets', okay_with_pets: 'Okay with Pets' },
  preferred_gender: { male: 'Wants Male Roommate', female: 'Wants Female Roommate', no_preference: 'No Gender Preference' },
};

export default function Profile() {
  const { userId } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isOwn = !userId || userId === currentUser?.id;
  const targetId = userId || currentUser?.id;

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', bio: '', major: '', year: '', phone: '', instagram: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!targetId) return;
    api.get(`/profile/${targetId}`).then(res => {
      setProfile(res.data);
      setForm({
        name: res.data.name || '',
        bio: res.data.bio || '',
        major: res.data.major || '',
        year: res.data.year || '',
        phone: res.data.phone || '',
        instagram: res.data.instagram || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [targetId]);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (avatarFile) {
        const fd = new FormData();
        fd.append('avatar', avatarFile);
        const res = await api.post('/profile/avatar', fd);
        setProfile(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      }
      await api.put('/profile', form);
      await refreshUser();
      setEditing(false);
      setToast('Profile updated!');
      setTimeout(() => setToast(''), 3000);
    } catch {}
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="page-container flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container text-center py-20">
        <p className="text-[#999999]">Profile not found</p>
      </div>
    );
  }

  const lifestyleTags = Object.entries(LIFESTYLE_LABELS)
    .filter(([key]) => profile[key])
    .map(([key]) => LIFESTYLE_LABELS[key][profile[key]])
    .filter(Boolean);

  return (
    <div className="page-container max-w-3xl mx-auto">
      {toast && (
        <div className="fixed top-20 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="card overflow-visible">
        {/* Cover */}
        <div className="h-32 rounded-t-lg overflow-hidden relative" style={{ background: 'linear-gradient(to right, rgba(200,168,0,0.15), #F0F0F0)' }}>
          {isOwn && editing && (
            <label className="absolute inset-0 flex items-center justify-center cursor-pointer group">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded px-3 py-1.5 text-sm text-white">
                Change Avatar
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
            </label>
          )}
        </div>

        <div className="px-6 pb-6">
          {/* Avatar */}
          <div className="-mt-12 mb-4 flex items-end justify-between">
            <div className="relative">
              {avatarPreview || profile.avatar_url ? (
                <img
                  src={avatarPreview || profile.avatar_url}
                  alt={profile.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-white shadow-md flex items-center justify-center font-black text-3xl" style={{ backgroundColor: 'rgba(200,168,0,0.12)', color: '#C8A800' }}>
                  {profile.name?.[0]?.toUpperCase()}
                </div>
              )}
              {isOwn && editing && (
                <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#C8A800] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#B89700] transition-colors">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                </label>
              )}
            </div>

            {isOwn ? (
              <button
                onClick={() => editing ? handleSave() : setEditing(true)}
                disabled={saving}
                className="btn-primary py-2"
              >
                {saving ? 'Saving...' : editing ? 'Save Profile' : 'Edit Profile'}
              </button>
            ) : (
              <button
                onClick={async () => {
                  try {
                    const res = await api.post('/messages/start', { other_user_id: profile.id, conversation_type: 'roommate' });
                    navigate(`/messages/${res.data.id}`);
                  } catch {}
                }}
                className="btn-primary py-2"
              >
                Message
              </button>
            )}
          </div>

          {/* Info */}
          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Full Name</label>
                  <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="label">Major</label>
                  <input className="input" placeholder="Computer Science" value={form.major} onChange={e => setForm({ ...form, major: e.target.value })} />
                </div>
                <div>
                  <label className="label">Year</label>
                  <select className="input" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })}>
                    <option value="">Select...</option>
                    {['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate', 'Alumni'].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" type="tel" placeholder="(319) 555-0100" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="label">Instagram</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]">@</span>
                    <input className="input pl-7" value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })} />
                  </div>
                </div>
              </div>
              <div>
                <label className="label">Bio</label>
                <textarea className="input h-24 resize-none" placeholder="Tell potential roommates about yourself..." value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
              </div>
              <button onClick={() => setEditing(false)} className="btn-ghost border border-[#DDDDDD]">Cancel</button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-black text-[#1B1B1B]">{profile.name}</h1>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="badge-gray">{userTypeLabel(profile.user_type)}</span>
                {profile.major && <span className="text-[#555555] text-sm">{profile.major}</span>}
                {profile.year && <span className="text-[#555555] text-sm">· {profile.year}</span>}
                <span className="text-[#999999] text-sm">· {profile.school}</span>
              </div>

              {profile.bio && <p className="text-[#444444] mt-4">{profile.bio}</p>}

              {/* Contact */}
              {isOwn && (
                <div className="flex gap-4 mt-4 text-sm text-[#777777]">
                  {profile.phone && <span>{profile.phone}</span>}
                  {profile.instagram && <span>@{profile.instagram}</span>}
                </div>
              )}
            </>
          )}

          {/* Lifestyle tags */}
          {lifestyleTags.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-[#1B1B1B] mb-3">Lifestyle</h3>
              <div className="flex flex-wrap gap-2">
                {lifestyleTags.map(tag => (
                  <span key={tag} className="badge-gray">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Hometown & extracurriculars */}
          {(profile.hometown || profile.extracurriculars) && (
            <div className="mt-4 space-y-2">
              {profile.hometown && (
                <p className="text-sm text-[#555555]">
                  <span className="font-semibold text-[#1B1B1B]">Hometown</span> · {profile.hometown}
                </p>
              )}
              {profile.extracurriculars && (
                <p className="text-sm text-[#555555]">
                  <span className="font-semibold text-[#1B1B1B]">Extracurriculars</span> · {profile.extracurriculars}
                </p>
              )}
            </div>
          )}

          {/* Budget */}
          {(profile.budget_min || profile.budget_max) && (
            <div className="mt-4">
              <h3 className="font-semibold text-[#1B1B1B] mb-2">Budget</h3>
              <p className="text-[#444444]">
                ${profile.budget_min}–${profile.budget_max}/mo
                {profile.move_in_date && ` · Move in ${formatDate(profile.move_in_date)}`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
